import { FC, InputHTMLAttributes, useMemo } from 'react';
import { CardType, getCardType, validatePAN } from 'utils/cardUtils';

interface CardNumberInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  name: string;
  showLogo?: boolean;
  /** The brand from the BIN lookup; preferred over prefix detection. */
  brand?: string | null;
}

const BRAND_LOGOS: Record<string, string> = {
  VISA: '/icons/visa.svg',
  MASTERCARD: '/icons/mastercard.svg',
  VERVE: '/icons/verve.svg',
  AFRIGO: '/icons/afrigo.svg',
};

/** BIN lookup brands vary in casing and punctuation, e.g. "AfriGo". */
const normalizeBrand = (brand?: string | null): string =>
  brand ? brand.replace(/[^a-z]/gi, '').toUpperCase() : '';

const CardNumberInput: FC<CardNumberInputProps> = ({
  label,
  value,
  onChange,
  name,
  onBlur,
  showLogo = true,
  brand,
}) => {
  const cardType = useMemo(() => {
    return typeof value === 'string' ? getCardType(value) : CardType.UNKNOWN;
  }, [value]);

  const isValidCard = useMemo(() => {
    return typeof value === 'string' && value.length > 0
      ? validatePAN(value)
      : null;
  }, [value]);

  const binBrand = normalizeBrand(brand);
  const binLogo = BRAND_LOGOS[binBrand];
  // Prefix detection is only a fallback while the BIN lookup is pending or
  // failed, so it still waits for a Luhn-valid number.
  const fallbackLogo = isValidCard ? BRAND_LOGOS[cardType] : undefined;
  const cardLogo = binLogo ?? fallbackLogo ?? null;
  const cardLogoAlt = binLogo ? binBrand : cardType;

  const getCardBorderColor = () => {
    if (!isValidCard) return 'border-gray-300';
    return isValidCard ? 'border-green-500' : 'border-red-500';
  };

  return (
    <div className="relative h-14 w-full bg-transparent">
      <input
        autoComplete="off"
        type="text"
        inputMode="numeric"
        name={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder=" "
        className={`peer absolute left-0 top-0 w-full border rounded-md px-3 pt-5 pb-2 text-base text-gray-900 focus:outline-none focus:ring-0 focus:border-blue-500 bg-transparent z-30 transition-colors ${getCardBorderColor()} ${
          cardLogo ? 'pr-12' : ''
        }`}
      />
      <label
        className={`absolute left-3 top-1 text-gray-500 text-xs transform transition-all duration-200 
          peer-placeholder-shown:top-5 peer-placeholder-shown:text-xs peer-placeholder-shown:text-gray-400 
          peer-focus:top-1 peer-focus:text-blue-500 z-10`}
      >
        {label}
      </label>

      {showLogo && cardLogo && (
        <img
          src={cardLogo}
          alt={cardLogoAlt}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 h-8 w-8 z-20"
        />
      )}
    </div>
  );
};

export default CardNumberInput;
